import {
  CreditTrackerAdminPage as CreditTrackerAdminPageProps,
  SiteSettings
} from '@/@types/content';
import withNav from '../hocs/withNav';
import ContentArea from '../cms/ContentArea';
import CreditTrackerAdminTable from '../ui/Tables/CreditTrackerAdminTable';
import useCreditTrackerAdmin from '../ui/CreditTrackerAdmin/hooks/useCreditTrackerAdminHook';
import classnames from 'classnames';
import FilterRow, { FilterRowRefs } from '../ui/Filter/FilterRow';
import SearchResultsFilterOverlay from '../ui/SearchResults/SearchResultsFilterOverlay';
import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../ui/Buttons/Button';
import Pagination from '../ui/Pagination';
import LoadingDots from '../ui/Buttons/LoadingDots';
import Link from '../ui/Link';
import useToast from '@/hooks/useToast';
import Shimmer from '../ui/Shimmer';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import Divider from '../ui/Divider';
import Icon from '../ui/Icon';

interface CreditTrackerAdminPagePropsExtended extends CreditTrackerAdminPageProps {
  isPLIEmployee: boolean;
}

const CreditTrackerAdminPageComponent: React.FC<
  CreditTrackerAdminPagePropsExtended & SiteSettings
> = ({
  browserTitle,
  mainContentArea,
  myCreditTrackerPageReference,
  pageDescriptionText,
  downloadExcelLabel,
  setupIncompleteText,
  filterDescriptionText,
  isPLIEmployee,
  emailComplianceTooltipBody,
  emailComplianceTooltipHeader,
  creditTrackerIncompleteTooltipBody,
  creditTrackerIncompleteTooltipHeader
}) => {
  const auth = useAuth();
  const [params] = useSearchParams();
  const [query, setQuery] = useState('');
  const filterRowRef = useRef<FilterRowRefs>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isDownloadAllLoading, setIsDownloadAllLoading] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const { showSuccessMessage, showFailMessage } = useToast();

  const orgId = useMemo(() => params.get('OrgId'), [params]);
  const userId = useMemo(() => params.get('UserId'), [params]);

  const {
    members,
    isRosterDataLoading,
    isTrackerDataLoading,
    allFilters,
    selectedFilters,
    currentPage,
    totalPages,
    totalResults,
    defaultLabels,
    goToPreviousPage,
    goToNextPage,
    setQueryFilter,
    onFilterToggle,
    clearAllFilters,
    downloadDataForAllRosterMembersFile,
    isDownloadAllSuccess,
    isDownloadAllError,
    setCurrentPage
  } = useCreditTrackerAdmin({ setupIncompleteText, filterDescriptionText });

  const handleOnFilterMenuOpen = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  useEffect(() => {
    if (!isFilterPanelOpen && filterRowRef.current) {
      filterRowRef.current.focusOnAllFiltersButton();
    }
  }, [isFilterPanelOpen]);

  const refetchData = (e?: FormEvent) => {
    e?.preventDefault();
    setCurrentPage(1);
    setQueryFilter(query);
  };

  useEffect(() => {
    if (isDownloadAllLoading && isDownloadAllSuccess) {
      setIsDownloadAllLoading(false);
      showSuccessMessage('File downloaded successfully');
    }
    if (isDownloadAllLoading && isDownloadAllError) {
      setIsDownloadAllLoading(false);
      showFailMessage('Error downloading file for all roster members');
    }
  }, [
    isDownloadAllLoading,
    isDownloadAllSuccess,
    isDownloadAllError,
    showSuccessMessage,
    showFailMessage
  ]);

  useEffect(() => {
    if (
      auth?.isAuthenticated &&
      (auth?.user?.profile?.IsLMSAdmin as string).toLowerCase() === 'true' &&
      isPLIEmployee &&
      (orgId || userId)
    ) {
      setShowAdvancedSearch(true);
    }
  }, [auth?.isAuthenticated, auth?.user?.profile?.IsLMSAdmin, isPLIEmployee, orgId, userId]);

  const downloadAllData = useCallback(async () => {
    setIsDownloadAllLoading(true);
    await downloadDataForAllRosterMembersFile();
  }, [downloadDataForAllRosterMembersFile]);

  const downloadDataForAllRosterMembersButton = isDownloadAllLoading ? (
    <LoadingDots color="black" dotSize="small" />
  ) : (
    <Link
      as="button"
      text={downloadExcelLabel ?? defaultLabels.downloadExcelLabel}
      variant="bold-link-with-icon"
      icon="download"
      onClick={downloadAllData}
    />
  );

  const advancedSearch = (
    <>
      <div
        className="mb-4 flex w-full max-w-[627px] items-center gap-3 rounded-[8px] border-2 px-4 py-3 font-semibold text-red"
        role="alert"
      >
        <Icon name={'alert'} size="large" />
        <div>
          <div className="text-base mb-1 font-bold">Credit Tracker God Mode Tool</div>
          <div className="text-sm font-normal">
            View and Edit customer credit trackers with{' '}
            <span className="font-semibold">extreme caution</span>.<br />
            {orgId ? (
              <>
                <span className="font-semibold">Organization ID:</span> {orgId}
              </>
            ) : (
              userId && (
                <>
                  <span className="font-semibold">User ID:</span> {userId}
                </>
              )
            )}
          </div>
        </div>
      </div>
      <form className="mb-4 flex w-full flex-col gap-[16px] sm:flex-row">
        <input
          type="text"
          id="OrgId"
          name="OrgId"
          defaultValue={orgId ?? ''}
          className={classnames(
            'text-2 h-[64px] rounded-[8px] border border-gray bg-gray-lightest px-[16px]',
            'w-full lg:w-[500px]',
            'placeholder:text-gray-dark'
          )}
          placeholder="Organization ID"
        />
        <Button type="submit" className="h-[64px]" color="black" label="Submit" />
      </form>
      <form className="mb-8 flex w-full flex-col gap-[16px] sm:flex-row">
        <input
          type="text"
          id="UserId"
          name="UserId"
          defaultValue={userId ?? ''}
          className={classnames(
            'text-2 h-[64px] rounded-[8px] border border-gray bg-gray-lightest px-[16px]',
            'w-full lg:w-[500px]',
            'placeholder:text-gray-dark'
          )}
          placeholder="User ID"
        />
        <Button type="submit" className="h-[64px]" color="black" label="Submit" />
      </form>
      <Divider color="silver" className="mb-8" />
    </>
  );

  return (
    <>
      <ContentArea componentsProps={{ pageName: browserTitle }} components={mainContentArea} />
      <div className="container module-spacing">
        {showAdvancedSearch && advancedSearch}
        <section className="mb-[24px]">
          <p className="heading-4 mb-2">
            {!showAdvancedSearch && (auth?.user?.profile['PrimaryOrganizationName'] as string)}
          </p>
          <span className="text-2 text-gray">
            {pageDescriptionText ?? defaultLabels.pageDescriptionText}
          </span>
        </section>
        <form
          onSubmit={e => refetchData(e)}
          className="mb-[40px] flex w-full flex-col gap-[16px] sm:flex-row"
        >
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={classnames(
              'text-2 h-[64px] rounded-[8px] border border-gray bg-gray-lightest px-[16px]',
              'w-full lg:w-[500px]',
              'placeholder:text-gray-dark'
            )}
            placeholder="Search for a member"
            autoFocus
          />
          <Button
            type="submit"
            className="h-[64px]"
            color="black"
            label="Search"
            onClick={refetchData}
          />
        </form>
        <div className="mb-10">
          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-dark">
              {isRosterDataLoading ? <Shimmer className="h-6 w-16" /> : <>{totalResults} RESULTS</>}
            </label>
            <div className="h-4 w-px bg-gray-medium" />
            <label
              className="text-sm inline-block"
              aria-label="Download all data for all roster members"
            >
              {downloadDataForAllRosterMembersButton}
            </label>
          </div>
        </div>
        <section className="mb-[40px]">
          <FilterRow
            ref={filterRowRef}
            filters={selectedFilters}
            loading={isRosterDataLoading}
            onFilterMenuOpen={handleOnFilterMenuOpen}
            onFilterToggle={onFilterToggle}
            sortMenu={undefined}
          />
        </section>
        <CreditTrackerAdminTable
          isLoading={isRosterDataLoading}
          isTrackerDataLoading={isTrackerDataLoading}
          tableHeader={['Name', 'Jurisdiction', 'Compilance Period', 'Total for Period', '']}
          rows={members ?? []}
          myCreditTrackerPageUrl={myCreditTrackerPageReference?.url}
          emailComplianceTooltipBody={
            emailComplianceTooltipBody ?? defaultLabels.emailComplianceTooltipBody
          }
          emailComplianceTooltipHeader={
            emailComplianceTooltipHeader ?? defaultLabels.emailComplianceTooltipHeader
          }
          creditTrackerIncompleteTooltipBody={
            creditTrackerIncompleteTooltipBody ?? defaultLabels.creditTrackerIncompleteTooltipBody
          }
          creditTrackerIncompleteTooltipHeader={
            creditTrackerIncompleteTooltipHeader ??
            defaultLabels.creditTrackerIncompleteTooltipHeader
          }
        />
        <Pagination
          className="mt-12"
          count={currentPage}
          totalCount={totalPages}
          onClickPrev={goToPreviousPage}
          onClickNext={goToNextPage}
          prevDisabled={isRosterDataLoading || currentPage === 1}
          nextDisabled={isRosterDataLoading || currentPage === totalPages}
        />
      </div>
      <SearchResultsFilterOverlay
        isFilterPanelOpen={isFilterPanelOpen}
        setIsFilterPanelOpen={setIsFilterPanelOpen}
        filters={allFilters}
        sortValue=""
        sortOptions={[]}
        onSortChange={() => {}}
        onClear={clearAllFilters}
        fetching={isTrackerDataLoading}
      />
    </>
  );
};

const CreditTrackerAdminPage = withNav(CreditTrackerAdminPageComponent);
export default CreditTrackerAdminPage;
